import { format } from 'date-fns';
import { AnalyticsDate, AnalyticsId } from './Analytics';
import { UserTraffic } from './ButtonClicks';

export interface QRCodeScanMetadata {
	type?: string;
	name?: string; // 'default' or specific QR code name (optional for backward compatibility)
	locationId: string;
	exhibitId: string;
	platform: string;
	browser: string;
	appSession?: string;
	viewsBy: string;
	areaId?: string;
	organizationId?: string;
}

export interface QRCodeScanResponse {
	_id: AnalyticsId;
	type: string;
	udid: string;
	os: string;
	metadata: QRCodeScanMetadata;
	ip?: string;
	createdDate: AnalyticsDate;
	modifiedDate: AnalyticsDate;
	status?: number;
}

export interface QRCodeScanData {
	topicId: string;
	qrCodeId: string;
	qrCodeName: string;
	scans: UserTraffic[];
}

/**
 * Parses an AnalyticsDate into a JavaScript Date object
 * @param date - The AnalyticsDate to parse
 * @returns JavaScript Date object
 * @throws Error if date cannot be parsed
 */
const parseAnalyticsDate = (date: AnalyticsDate): Date => {
	if (typeof date === 'string') {
		const parsed = new Date(date);
		if (isNaN(parsed.getTime())) {
			throw new Error(`Invalid date string: ${date}`);
		}
		return parsed;
	}

	if (typeof date === 'object' && date.$date?.$numberLong) {
		const timestamp = parseInt(date.$date.$numberLong);
		if (isNaN(timestamp)) {
			throw new Error(`Invalid timestamp: ${date.$date.$numberLong}`);
		}
		return new Date(timestamp);
	}

	throw new Error('Invalid date format');
};

/**
 * This function aggregates QR code scans from the raw data by day.
 * Groups scans by QR code name (default or specific name) and aggregates by date.
 * For backward compatibility, scans without a 'name' field are treated as 'default' QR codes.
 *
 * @param rawData - QRCodeScanResponse[] data from the API (session and top_content entries with viewsBy: 'scan')
 * @param locationId - location ID to filter by
 * @returns an array of aggregated QR code scans by day
 */
export const aggregateQRCodeScans = (rawData: QRCodeScanResponse[], locationId: string): QRCodeScanData[] => {
	if (!rawData || !Array.isArray(rawData)) {
		return [];
	}

	if (!locationId) {
		throw new Error('locationId is required');
	}

	// Create a Map to organize scans by QR code name
	const qrCodeScansMap: { [qrCodeName: string]: { scans: UserTraffic[]; topicId: string } } = {};

	// Loop through each QR code scan in the raw data
	rawData.forEach((scanData) => {
		// Filter by location and ensure it's a scan
		if (scanData.metadata.locationId !== locationId || scanData.metadata.viewsBy !== 'scan') {
			return;
		}

		// Validate required fields
		if (!scanData.metadata.exhibitId) {
			console.warn('Skipping scan with missing required metadata:', scanData);
			return;
		}

		// Use QR code name as identifier, default to 'default' if name is not provided (backward compatibility)
		const qrCodeName = scanData.metadata.name || 'default';

		// Parse the created date with error handling
		let date: Date;
		try {
			date = parseAnalyticsDate(scanData.createdDate);
		} catch (error) {
			console.warn('Skipping scan with invalid date:', scanData, error);
			return;
		}

		// Set the key and display for this scan
		const dateAsKey = format(date, 'M/d');
		const display = format(date, 'MMM d, yyyy');

		// Initialize the QR code map if undefined
		if (!qrCodeScansMap[qrCodeName]) {
			qrCodeScansMap[qrCodeName] = {
				topicId: scanData.metadata.exhibitId,
				scans: [],
			};
		}

		// Look for an existing scan with the same key
		const existingItem = qrCodeScansMap[qrCodeName].scans.find((scan) => scan.key === dateAsKey);

		// If a scan with the same date/key exists
		if (existingItem) {
			// Increment its value by 1
			existingItem.value += 1;
		} else {
			// Otherwise, add a new scan with a value of 1
			qrCodeScansMap[qrCodeName].scans.push({
				key: dateAsKey,
				value: 1,
				display,
				timestamp: date.getTime(),
			});
		}
	});

	// Convert the map to the final array format and sort
	return Object.keys(qrCodeScansMap).map((qrCodeName) => ({
		topicId: qrCodeScansMap[qrCodeName].topicId,
		qrCodeId: `${qrCodeName}_${qrCodeScansMap[qrCodeName].topicId}`,
		qrCodeName,
		scans: qrCodeScansMap[qrCodeName].scans.sort((a, b) => a.timestamp - b.timestamp),
	}));
};
